import { z } from "zod";
import {
  DISPATCH_CONTINUING_EDUCATION_REQUIRED_OPTIONS,
  DISPATCH_STATE_REQUIRE_CERTIFICATION_OPTIONS,
} from "./dispatchConstants";

export const DISPATCH_CAPABILITIES_PART_COUNT = 3;

export type DispatchCapabilitiesPart = 1 | 2 | 3;

export const registrationDispatchFieldsSchema = z.object({
  dispatchFacilityClassification: z.string().optional().default(""),
  dispatchDisciplinesServedByAgency: z.array(z.string()).default([]),
  dispatchMainAreaOfResponsibility: z.string().optional().default(""),
  dispatchEstPopulationOfResponsibilityArea: z.string().optional().default(""),
  dispatchComputerAidedDispatchSystemUsed: z.string().optional().default(""),
  dispatchComputerAidedDispatchSystemUsedOther: z.string().optional().default(""),
  dispatchCanDetermineCallerLatLongGis: z.boolean().nullable().default(null),
  dispatchProgramsUsedByAgency: z.array(z.string()).default([]),
  dispatchRadioSystemsUsedByAgency: z.array(z.string()).default([]),
  dispatchRadioSystemsUsedByAgencyOther: z.string().optional().default(""),
  dispatchPublicNotificationSystemUsed: z.string().optional().default(""),
  dispatchPublicNotificationSystemUsedOther: z.string().optional().default(""),
  dispatchIsContinuingEducationRequired: z.string().optional().default(""),
  dispatchHasAccessToEmergencyTopics: z.array(z.string()).default([]),
  dispatchSopSogForEmergencyTopics: z.array(z.string()).default([]),
  dispatchTrainingCoversEmergencyTopics: z.array(z.string()).default([]),
  dispatchCompletedCoursesOnEmergencyTopics: z.array(z.string()).default([]),
  dispatchIsRadioSystemEncrypted: z.boolean().nullable().default(null),
  dispatchIsRadioSystemP25Compliant: z.boolean().nullable().default(null),
  dispatchDoesStateRequireCertification: z.string().optional().default(""),
  dispatchDoesStateRequireCertificationOther: z.string().optional().default(""),
});

function requireDispatchYesNo(
  value: boolean | null,
  path: keyof RegistrationDispatchFieldValues,
  message: string,
  ctx: z.RefinementCtx,
): void {
  if (value === null) {
    ctx.addIssue({ code: "custom", path: [path], message });
  }
}

export type RegistrationDispatchFieldValues = z.infer<typeof registrationDispatchFieldsSchema>;

export const registrationDispatchDefaults = (): RegistrationDispatchFieldValues =>
  registrationDispatchFieldsSchema.parse({});

const NON_NEGATIVE_INTEGER_PATTERN = /^\d+$/;

function validateNonNegativeIntegerField(
  value: string,
  path: keyof RegistrationDispatchFieldValues,
  ctx: z.RefinementCtx,
  options: { required: boolean; requiredMessage: string; invalidMessage: string },
): void {
  const trimmed = value.trim();
  if (!trimmed) {
    if (options.required) {
      ctx.addIssue({ code: "custom", path: [path], message: options.requiredMessage });
    }
    return;
  }
  if (!NON_NEGATIVE_INTEGER_PATTERN.test(trimmed)) {
    ctx.addIssue({ code: "custom", path: [path], message: options.invalidMessage });
  }
}

function validateDispatchCapabilitiesPart1(
  v: RegistrationDispatchFieldValues,
  ctx: z.RefinementCtx,
): void {
  if (!v.dispatchFacilityClassification.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchFacilityClassification"],
      message: "Please select how your ECC/PSAP is classified",
    });
  }

  if (v.dispatchDisciplinesServedByAgency.length === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchDisciplinesServedByAgency"],
      message: "Select at least one discipline",
    });
  }
}

function validateDispatchCapabilitiesPart2(
  v: RegistrationDispatchFieldValues,
  ctx: z.RefinementCtx,
): void {
  validateNonNegativeIntegerField(
    v.dispatchEstPopulationOfResponsibilityArea,
    "dispatchEstPopulationOfResponsibilityArea",
    ctx,
    {
      required: false,
      requiredMessage: "",
      invalidMessage: "Please enter a whole number (0 or greater)",
    },
  );

  if (!v.dispatchMainAreaOfResponsibility.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchMainAreaOfResponsibility"],
      message: "Please select your main area of responsibility",
    });
  }

  const cadSystem = v.dispatchComputerAidedDispatchSystemUsed.trim();
  if (!cadSystem) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchComputerAidedDispatchSystemUsed"],
      message: "Please select the CAD system your agency uses",
    });
  } else if (
    cadSystem === "Other" &&
    !v.dispatchComputerAidedDispatchSystemUsedOther.trim()
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchComputerAidedDispatchSystemUsedOther"],
      message: "Please specify the CAD system",
    });
  }

  requireDispatchYesNo(
    v.dispatchCanDetermineCallerLatLongGis,
    "dispatchCanDetermineCallerLatLongGis",
    "Please indicate whether you can determine the lat/long GIS position of a caller",
    ctx,
  );

  const notificationSystem = v.dispatchPublicNotificationSystemUsed.trim();
  if (!notificationSystem) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchPublicNotificationSystemUsed"],
      message: "Please select your public notification system",
    });
  } else if (
    notificationSystem === "Other" &&
    !v.dispatchPublicNotificationSystemUsedOther.trim()
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchPublicNotificationSystemUsedOther"],
      message: "Please specify the public notification system",
    });
  }
}

function validateDispatchCapabilitiesPart3(
  v: RegistrationDispatchFieldValues,
  ctx: z.RefinementCtx,
): void {
  const certification = v.dispatchDoesStateRequireCertification.trim();
  if (
    !certification ||
    !DISPATCH_STATE_REQUIRE_CERTIFICATION_OPTIONS.includes(
      certification as (typeof DISPATCH_STATE_REQUIRE_CERTIFICATION_OPTIONS)[number],
    )
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchDoesStateRequireCertification"],
      message: "Please indicate whether your state requires telecommunicator certification",
    });
  } else if (certification === "Other" && !v.dispatchDoesStateRequireCertificationOther.trim()) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchDoesStateRequireCertificationOther"],
      message: "Please specify the certification requirement",
    });
  }

  const continuingEd = v.dispatchIsContinuingEducationRequired.trim();
  if (
    !continuingEd ||
    !DISPATCH_CONTINUING_EDUCATION_REQUIRED_OPTIONS.includes(
      continuingEd as (typeof DISPATCH_CONTINUING_EDUCATION_REQUIRED_OPTIONS)[number],
    )
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["dispatchIsContinuingEducationRequired"],
      message: "Please indicate whether continuing education is required",
    });
  }

  // Converted Yes/No → select-all: empty selection is allowed (none of the above).
}

function validateDispatchCapabilitiesPart(
  part: DispatchCapabilitiesPart,
  v: RegistrationDispatchFieldValues,
  ctx: z.RefinementCtx,
): void {
  if (part === 1) validateDispatchCapabilitiesPart1(v, ctx);
  else if (part === 2) validateDispatchCapabilitiesPart2(v, ctx);
  else validateDispatchCapabilitiesPart3(v, ctx);
}

export function registrationDispatchCapabilitiesPartSchema(part: DispatchCapabilitiesPart) {
  return registrationDispatchFieldsSchema.superRefine((v, ctx) => {
    validateDispatchCapabilitiesPart(part, v, ctx);
  });
}

export function firstInvalidDispatchCapabilitiesPart(
  values: RegistrationDispatchFieldValues,
): DispatchCapabilitiesPart {
  for (let part = 1 as DispatchCapabilitiesPart; part <= DISPATCH_CAPABILITIES_PART_COUNT; part++) {
    if (!registrationDispatchCapabilitiesPartSchema(part).safeParse(values).success) {
      return part;
    }
  }
  return 1;
}

export function registrationDispatchCapabilitiesStepSchema() {
  return registrationDispatchFieldsSchema.superRefine((v, ctx) => {
    validateDispatchCapabilitiesPart1(v, ctx);
    validateDispatchCapabilitiesPart2(v, ctx);
    validateDispatchCapabilitiesPart3(v, ctx);
  });
}
